import { type ReactNode, useId } from "react";

import { cn } from "../foundations/cn.js";

/** What a control inside a Field wires itself up with. */
export interface FieldControlProps {
  /** The control's id: the label points at it. */
  id: string;
  /** The label's id, for a control named by `aria-labelledby` (a radio group, a slider). */
  labelId: string;
  /** The helper's or error's id, for `aria-describedby`; undefined when there is neither. */
  describedBy: string | undefined;
  invalid: boolean;
}

export interface FieldProps {
  /** Shown above the control and used as its name. Always required, even when hidden. */
  label: string;
  /** Hides the label visually; screen readers still hear it. */
  hideLabel?: boolean;
  /** One line under the control: format, limits, what happens next. */
  helper?: string;
  /** What's wrong, in words. Replaces the helper and marks the control invalid. */
  error?: string;
  /** Marks the control invalid without words. */
  invalid?: boolean;
  /**
   * `control`: the label is a `<label>` for one focusable control. `group`: the label names a set
   * (radios, a slider's thumbs) through `aria-labelledby`, since a `<label>` can point at only one.
   */
  kind?: "control" | "group";
  /** The control's id; one is made when left out. */
  id?: string;
  /** Something on the label's line, after it: a link ("Forgot it?"), a count. */
  labelEnd?: ReactNode;
  /** Renders the control with the ids it needs. */
  children: (control: FieldControlProps) => ReactNode;
  className?: string;
}

/**
 * A control's label, helper and error in one column. Input, Textarea and the other form controls
 * are built on it; use it directly to give a custom control the same wiring.
 */
export function Field({
  label,
  hideLabel = false,
  helper,
  error,
  invalid: invalidProp = false,
  kind = "control",
  id,
  labelEnd,
  children,
  className,
}: FieldProps) {
  const autoId = useId();
  const controlId = id ?? autoId;
  const labelId = `${controlId}-label`;
  const noteId = `${controlId}-note`;
  const note = error ?? helper;
  const invalid = invalidProp || error !== undefined;
  const labelClass = cn(
    "font-body text-body-b3-strong text-text-secondary",
    hideLabel && "sr-only",
  );
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div className={cn("flex items-baseline justify-between gap-3", hideLabel && "contents")}>
        {kind === "control" ? (
          <label id={labelId} htmlFor={controlId} className={labelClass}>
            {label}
          </label>
        ) : (
          <span id={labelId} className={labelClass}>
            {label}
          </span>
        )}
        {labelEnd}
      </div>
      {children({ id: controlId, labelId, describedBy: note ? noteId : undefined, invalid })}
      {note && (
        <p
          id={noteId}
          className={cn(
            "font-body text-body-b3",
            error !== undefined ? "text-status-danger" : "text-text-tertiary",
          )}
        >
          {note}
        </p>
      )}
    </div>
  );
}
